/**
 * sentry.ts — Error monitoring initialization
 *
 * Sentry is active only in production (import.meta.env.PROD) when
 * VITE_SENTRY_DSN is set. In development it is silently disabled.
 *
 * Privacy rules applied:
 *   - No lesson content in breadcrumbs (filtered in beforeBreadcrumb)
 *   - User ID is tagged; email/name are NOT sent to Sentry
 *   - source maps are uploaded via vite build config (build.sourcemap: true)
 */

import * as Sentry from '@sentry/react';

const APP_VERSION = '0.1.0-beta';

// Breadcrumb keys whose values may contain sensitive lesson content
const SENSITIVE_KEYS = ['skill', 'lesson', 'content', 'text', 'topic', 'instructions', 'message', 'feedback'];

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  if (!dsn) {
    // Sentry disabled — no DSN configured. This is expected in development.
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // 'development' | 'production'
    release: `teachbud@${APP_VERSION}`,
    // Only collect errors in production; disable in dev to reduce noise
    enabled: import.meta.env.PROD,
    // Capture unhandled promise rejections
    integrations: [Sentry.browserTracingIntegration()],
    // Performance: 10% sample rate in production; 100% in staging
    tracesSampleRate: 0.1,

    // Remove sensitive lesson content from all breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        for (const key of SENSITIVE_KEYS) {
          if (key in breadcrumb.data) {
            breadcrumb.data[key] = '[redacted]';
          }
        }
      }
      // Strip full fetch URLs so they don't inadvertently expose content
      if (breadcrumb.category === 'fetch' && breadcrumb.data?.url) {
        // Keep the path but strip query strings that might contain content
        try {
          const url = new URL(breadcrumb.data.url as string, window.location.origin);
          breadcrumb.data.url = url.pathname;
        } catch {
          breadcrumb.data.url = '[url]';
        }
      }
      return breadcrumb;
    },

    // Strip sensitive data from captured events
    beforeSend(event) {
      // Don't send events in dev even if DSN is set (shouldn't happen due to `enabled` flag)
      if (import.meta.env.DEV) return null;
      return event;
    },
  });
}

/**
 * Set the authenticated user context for future error reports.
 * Only the userId is sent — email/name are withheld.
 */
export function setSentryUser(userId: string | null) {
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture an exception explicitly (e.g. from the ErrorBoundary).
 */
export { Sentry };
